import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "./useDebounce";

export type SearchResultType = "template" | "action" | "trigger" | "connector_def" | "workflow";

export interface SearchResult {
  id: string;
  type: SearchResultType;
  name: string;
  description?: string;
  slug?: string;
  category?: string;
  tags?: string[];
  score: number;
}

export interface SearchResponse {
  query: string;
  totalHits: number;
  processingTimeMs?: number;
  cached?: boolean;
  results: SearchResult[];
}

interface SearchApiResponse {
  code: number;
  message: string;
  data: {
    query: string;
    totalHits: number;
    processingTimeMs?: number;
    cached?: boolean;
    results: SearchResult[];
  };
}

export interface UseSearchOptions {
  types?: SearchResultType[];
  limit?: number;
  offset?: number;
  debounceMs?: number;
  minQueryLength?: number;
  enabled?: boolean;
}

export function useSearch(query: string, options: UseSearchOptions = {}) {
  const { types = [], limit = 10, offset = 0, debounceMs = 300, minQueryLength = 2, enabled = true } = options;

  const debouncedQuery = useDebounce(query, debounceMs);
  const shouldSearch = enabled && debouncedQuery.length >= minQueryLength;

  const searchQuery = useQuery({
    queryKey: ["search", debouncedQuery, types, limit, offset],
    queryFn: async (): Promise<SearchResponse> => {
      const params = new URLSearchParams();
      params.set("q", debouncedQuery);
      params.set("limit", String(limit));
      params.set("offset", String(offset));

      types.forEach(type => {
        params.append("types", type);
      });

      const response = await authenticatedFetch<SearchApiResponse>(`${V2_ENDPOINT}/v1/search?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.code !== 0) {
        throw new Error(`Search API error: ${apiResponse.message}`);
      }

      return apiResponse.data;
    },
    enabled: shouldSearch,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  return {
    results: searchQuery.data?.results ?? [],
    totalHits: searchQuery.data?.totalHits ?? 0,
    processingTimeMs: searchQuery.data?.processingTimeMs,
    cached: searchQuery.data?.cached,
    isLoading: searchQuery.isLoading && shouldSearch,
    isFetching: searchQuery.isFetching,
    error: searchQuery.error,
    shouldUseApiSearch: shouldSearch,
    debouncedQuery,
  };
}
